import axios from 'axios';

// When REACT_APP_API_URL is empty the browser uses the same origin, which
// means Nginx forwards /api/* to the backend.  Locally, set it to
// http://localhost:5000 via the .env file.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const MIN_CHUNK_SIZE  =    10 * 1024 * 1024; // 10 MB  – S3 minimum for non-last parts
const MAX_CHUNK_SIZE  =  5000 * 1024 * 1024; // 5 GB   – S3 maximum per part
const MAX_PARTS       =  9000;               // S3 allows 10,000; stay well under
const MAX_CONCURRENT  =     5;               // parallel S3 PUT connections
const MAX_RETRIES     =     4;               // per-part retry attempts before giving up

// Exponential backoff: 1s, 2s, 4s, 8s
const retryDelay = (attempt) =>
  new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));

class S3MultipartUploader {
  constructor(file, ticketNumber, onProgress) {
    this.file = file;
    this.ticketNumber = ticketNumber;
    this.onProgress = onProgress;
    this.uploadId = null;
    this.key = null;
    this.parts = [];
    this.aborted = false;
    this.abortController = new AbortController();

    // Chunk size: large enough to stay under 9,000 parts for any file up to 5 TB,
    // but never below S3's 10 MB minimum or above S3's 5 GB maximum per part.
    // Examples:  50 MB  →  10 MB chunks  (5 parts)
    //             1 GB  →  10 MB chunks  (100 parts)
    //            10 GB  →  12 MB chunks  (~862 parts)
    //           200 GB  → 234 MB chunks  (~874 parts)
    //             1 TB  → 1.2 GB chunks  (~874 parts)
    //             5 TB  →   5 GB chunks  (~1024 parts)
    this.chunkSize = Math.min(
      MAX_CHUNK_SIZE,
      Math.max(MIN_CHUNK_SIZE, Math.ceil(file.size / MAX_PARTS))
    );

    // Bytes uploaded per part, used to compute accurate overall progress.
    this.partBytesUploaded = {};
  }

  _reportProgress() {
    const bytesUploaded = Object.values(this.partBytesUploaded).reduce((a, b) => a + b, 0);
    const overallPercent = Math.min(100, (bytesUploaded / this.file.size) * 100);
    this.onProgress({ overallPercent, bytesUploaded, totalBytes: this.file.size });
  }

  async initiateUpload() {
    const response = await axios.post(`${API_BASE_URL}/api/upload/initiate`, {
      ticketNumber: this.ticketNumber,
      fileName: this.file.name
    });
    
    this.uploadId = response.data.uploadId;
    this.key = response.data.key;
    
    return response.data;
  }

  async uploadPart(chunk, partNumber) {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (this.aborted) throw new Error('Upload aborted');
      try {
        // Reset progress for this part on retry so the bar doesn't jump back
        this.partBytesUploaded[partNumber] = 0;

        // Get a fresh presigned URL on every attempt (previous one may have expired)
        const urlResponse = await axios.post(`${API_BASE_URL}/api/upload/presigned-url`, {
          key: this.key,
          uploadId: this.uploadId,
          partNumber: partNumber
        });

        const presignedUrl = urlResponse.data.url;

        const uploadResponse = await axios.put(presignedUrl, chunk, {
          headers: { 'Content-Type': 'application/octet-stream' },
          signal: this.abortController.signal,
          onUploadProgress: (progressEvent) => {
            this.partBytesUploaded[partNumber] = progressEvent.loaded;
            this._reportProgress();
          }
        });

        this.partBytesUploaded[partNumber] = chunk.size;
        this._reportProgress();

        const etag = uploadResponse.headers.etag.replace(/"/g, '');
        return { partNumber, etag };
      } catch (err) {
        if (this.aborted) throw new Error('Upload aborted');
        if (attempt === MAX_RETRIES) throw err; // out of retries
        await retryDelay(attempt);
      }
    }
  }

  async upload() {
    try {
      // Step 1: Initiate multipart upload
      await this.initiateUpload();

      const totalParts = Math.ceil(this.file.size / this.chunkSize);
      const results = new Array(totalParts);

      // Step 2: Upload parts with bounded concurrency.
      // Each worker picks the next available part until all are done.
      let nextPart = 1;
      const worker = async () => {
        while (nextPart <= totalParts) {
          if (this.aborted) throw new Error('Upload aborted');
          const partNumber = nextPart++;
          const start = (partNumber - 1) * this.chunkSize;
          const end = Math.min(start + this.chunkSize, this.file.size);
          const chunk = this.file.slice(start, end);
          results[partNumber - 1] = await this.uploadPart(chunk, partNumber);
        }
      };

      const concurrency = Math.min(MAX_CONCURRENT, totalParts);
      await Promise.all(Array.from({ length: concurrency }, worker));

      this.parts = results;

      // Step 3: Complete multipart upload
      const completeResponse = await axios.post(`${API_BASE_URL}/api/upload/complete`, {
        key: this.key,
        uploadId: this.uploadId,
        parts: this.parts
      });

      return completeResponse.data;
    } catch (error) {
      // Abort upload on error
      if (this.uploadId && !this.aborted) {
        await this.abort();
      }
      throw error;
    }
  }

  async abort() {
    this.aborted = true;
    this.abortController.abort(); // immediately cancel all in-flight PUT requests
    if (this.uploadId) {
      await axios.post(`${API_BASE_URL}/api/upload/abort`, {
        key: this.key,
        uploadId: this.uploadId
      });
    }
  }
}

export default S3MultipartUploader;
